import { useState, useRef, useMemo } from "react";
import { Link, useLocation } from "wouter";
import { Loader2, CheckCircle2 } from "lucide-react";
import LogoAnimation from "@/components/LogoAnimation";
// v7.05 — restyled to the admoji.com/ads look, matching the rest of the flow.
import {
  MUTED, PINK,
  pageStyle, cardStyle, monoLabelStyle, h1Style, serifStyle, subStyle,
  labelStyle, fieldStyle, buttonStyle, errorStyle, footerStyle,
  fieldFocus, fieldBlur, btnHoverOn, btnHoverOff,
} from "./auth-theme";

export default function ResetPassword() {
  const [, setLocation] = useLocation();
  const token = useMemo(() => new URLSearchParams(window.location.search).get("token") || "", []);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting.current) return;
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    submitting.current = true;
    setLoading(true);
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || "Something went wrong. Try again.");
        return;
      }
      setDone(true);
      setTimeout(() => setLocation("/login"), 2200);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      submitting.current = false;
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={pageStyle}
      data-testid="reset-password-page"
    >
      <div className="w-full" style={{ maxWidth: 400 }}>
        <div className="flex justify-center mb-7">
          <LogoAnimation size={126} />
        </div>

        <div style={cardStyle}>
          {!token ? (
            <div data-testid="reset-no-token">
              <div style={monoLabelStyle}>Ad Manager</div>
              <h1 style={h1Style}>
                Invalid{" "}
                <span style={serifStyle}>link.</span>
              </h1>
              <p style={subStyle}>This reset link is missing its token. Request a new one.</p>
              <Link
                href="/forgot-password"
                className="inline-block mt-6"
                style={{ fontSize: 12.5, fontWeight: 600, color: PINK }}
              >
                Request a new link
              </Link>
            </div>
          ) : done ? (
            <div data-testid="reset-done">
              <CheckCircle2 className="h-9 w-9 mb-4" style={{ color: "#16a34a" }} />
              <h1 style={h1Style}>
                Password{" "}
                <span style={serifStyle}>updated.</span>
              </h1>
              <p style={subStyle}>Taking you to sign in…</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <div style={monoLabelStyle}>Ad Manager</div>
                <h1 style={h1Style}>
                  Choose a new{" "}
                  <span style={serifStyle}>password.</span>
                </h1>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="text-sm px-4 py-3" style={errorStyle} data-testid="reset-error">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="password" className="block mb-1.5" style={labelStyle}>
                    New password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    autoFocus
                    data-testid="input-password"
                    className="w-full h-11 px-3 outline-none transition-all"
                    style={fieldStyle}
                    onFocus={fieldFocus}
                    onBlur={fieldBlur}
                  />
                </div>

                <div>
                  <label htmlFor="confirm" className="block mb-1.5" style={labelStyle}>
                    Confirm password
                  </label>
                  <input
                    id="confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    data-testid="input-confirm"
                    className="w-full h-11 px-3 outline-none transition-all"
                    style={fieldStyle}
                    onFocus={fieldFocus}
                    onBlur={fieldBlur}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="button-reset"
                  className="w-full flex items-center justify-center transition-colors disabled:opacity-60"
                  style={buttonStyle}
                  onMouseEnter={(e) => btnHoverOn(e, loading)}
                  onMouseLeave={btnHoverOff}
                >
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating…</> : "Update password"}
                </button>

                <p className="text-center" style={{ fontSize: 12.5, color: MUTED }}>
                  Remembered it?{" "}
                  <Link href="/login" style={{ fontWeight: 600, color: PINK }}>Sign in</Link>
                </p>
              </form>
            </>
          )}
        </div>

        <p className="text-center mt-6" style={footerStyle}>Admoji Ad Manager</p>
      </div>
    </div>
  );
}
